import { toast } from 'react-hot-toast';

import { Loading } from '@/components/shared';
import { Button } from '@/components/ui/NewButton';
import { SettingRow, SettingsPanel } from '@/components/ui/settings-panel';
import { Switch } from '@/components/ui/switch';
import {
  type NotificationChannel,
  useNotificationPreferences,
} from '@/hooks/useNotificationPreferences';
import { apiErrorMessage } from '@/lib/api-client';

const channelLabels: Record<NotificationChannel, string> = {
  email: 'Email',
};

export function NotificationsPanel() {
  return (
    <div className="space-y-6">
      <NotificationRows />
    </div>
  );
}

function NotificationRows() {
  const {
    preferences,
    isLoading,
    isError,
    refetch,
    updatePreference,
  } = useNotificationPreferences();

  const pendingVariables = updatePreference.variables;

  const handleToggle = async (
    eventType: string,
    channel: NotificationChannel,
    enabled: boolean,
  ) => {
    try {
      await updatePreference.mutateAsync({ eventType, channel, enabled });
    } catch (err) {
      toast.error(apiErrorMessage(err) || 'Failed to update notification preferences');
    }
  };

  const handleRetry = async () => {
    const result = await refetch();
    if (result.error) {
      toast.error(apiErrorMessage(result.error) || 'Failed to load notification preferences');
    }
  };

  if (isLoading) {
    return (
      <SettingsPanel>
        <div className="flex min-h-32 items-center justify-center">
          <Loading text="Loading notification preferences" />
        </div>
      </SettingsPanel>
    );
  }

  if (isError) {
    return (
      <SettingsPanel>
        <div className="flex min-h-32 flex-col items-center justify-center gap-3 p-6 text-center">
          <p className="text-sm text-muted-foreground">
            Notification preferences could not be loaded.
          </p>
          <Button
            variant="outline"
            size="sm"
            text="Retry"
            onClick={() => {
              void handleRetry();
            }}
          />
        </div>
      </SettingsPanel>
    );
  }

  if (preferences.length === 0) {
    return (
      <SettingsPanel>
        <div className="flex min-h-32 items-center justify-center p-6 text-center">
          <p className="text-sm text-muted-foreground">
            There are no email notifications to configure.
          </p>
        </div>
      </SettingsPanel>
    );
  }

  return (
    <SettingsPanel>
      {preferences.map((preference) => {
        const channel: NotificationChannel = 'email';
        const channelEnabled = preference.channels[channel];
        if (channelEnabled === undefined) return null;

        const switchId = `notification-${preference.event_type}-${channel}`;
        const isPending =
          updatePreference.isPending &&
          pendingVariables?.eventType === preference.event_type &&
          pendingVariables?.channel === channel;

        return (
          <SettingRow
            key={preference.event_type}
            htmlFor={switchId}
            label={preference.label}
            description={preference.description}
          >
            <div className="flex h-9 items-center justify-end gap-3">
              <span className="text-sm text-muted-foreground">{channelLabels[channel]}</span>
              <Switch
                id={switchId}
                checked={channelEnabled}
                disabled={updatePreference.isPending}
                aria-label={`${preference.label} ${channelLabels[channel]}`}
                aria-busy={isPending}
                onCheckedChange={(checked) => {
                  void handleToggle(preference.event_type, channel, checked);
                }}
              />
            </div>
          </SettingRow>
        );
      })}
    </SettingsPanel>
  );
}